/**
 * An engagement model's snapshots, one row each, with what each one reads.
 *
 * The sibling of `TimelineBands`, for a model whose datasets are dates rather
 * than windows. On one shared line the snapshots' 90-day windows overlapped
 * into a single band and no tick could be told from another, so every snapshot
 * has its own row, named by its kind and date: the activity read before it,
 * the snapshot itself, and the 30 days in which a conversion counts for it.
 * One axis for all rows, so whether validation lands after the training run or
 * inside it is still read straight down the page.
 *
 * Geometry comes from `snapshotTimeline`, which is tested and judges nothing.
 * The one judgement drawn here is the overlap, which is said in words rather
 * than implied by two rows lining up.
 */
import React from 'react';

import {
  CONVERSION_DAYS,
  LOOKBACK_DAYS,
  SnapshotTimeline,
} from './snapshotTimeline';
import './datasetTimeline.css';

export function SnapshotBands({
  timeline,
  unit = 'leads',
}: {
  timeline: SnapshotTimeline;
  /** What a snapshot scores: an account model scores accounts. */
  unit?: 'leads' | 'accounts';
}) {
  const one = unit === 'accounts' ? 'An account' : 'A lead';
  return (
    <div className="dtl dtl--snapshots">
      {timeline.snapshots.map((snapshot) => {
        const kind = snapshot.kind === 'training' ? 'Training' : 'Validation';
        return (
          <div
            className="dtl-srow"
            key={`${snapshot.kind}-${snapshot.date}`}
            title={`${kind} snapshot ${snapshot.label}. ${one} that converts in the ${CONVERSION_DAYS} days after it is a conversion, read on the ${LOOKBACK_DAYS} days before it converted; any other is read on the ${LOOKBACK_DAYS} days before the snapshot.`}
          >
            <span className="dtl-slabel">
              <span
                className={`dtl-key dtl-key--${snapshot.kind}`}
                aria-hidden
              />
              <span className="dtl-name">{kind}</span>
              <span className="dtl-range">{snapshot.label}</span>
            </span>
            <div className="dtl-strack">
              {timeline.months.map((month) => (
                <span
                  key={month.label + month.leftPct}
                  className="dtl-guide"
                  style={{ left: `${month.leftPct}%` }}
                  aria-hidden
                />
              ))}
              <div
                className={`dtl-window dtl-window--${snapshot.kind}`}
                style={{
                  left: `${snapshot.windowLeftPct}%`,
                  width: `${snapshot.windowWidthPct}%`,
                }}
              />
              <div
                className={`dtl-outcome dtl-outcome--${snapshot.kind}`}
                style={{
                  left: `${snapshot.leftPct}%`,
                  width: `${snapshot.conversionWidthPct}%`,
                }}
              />
              <div
                className={`dtl-tick dtl-tick--${snapshot.kind}`}
                style={{ left: `${snapshot.leftPct}%` }}
              />
            </div>
          </div>
        );
      })}

      <div className="dtl-srow dtl-srow--axis" aria-hidden>
        <span className="dtl-slabel" />
        <div className="dtl-strack dtl-strack--axis">
          {timeline.months.map((month) => (
            <span
              key={month.label + month.leftPct}
              className="dtl-month"
              style={{ left: `${month.leftPct}%` }}
            >
              {month.label}
            </span>
          ))}
        </div>
      </div>

      <div className="dtl-legend">
        <span className="dtl-swatch dtl-swatch--window" aria-hidden />
        <span className="dtl-months">{LOOKBACK_DAYS} days of activity</span>
        <span className="dtl-swatch dtl-swatch--outcome" aria-hidden />
        <span className="dtl-months">
          {CONVERSION_DAYS}-day conversion window
        </span>
        {timeline.validationOverlaps && (
          <span className="dtl-months dtl-warn">
            Validation is inside the training run, so the same {unit} are scored
            twice.
          </span>
        )}
      </div>
    </div>
  );
}

export default SnapshotBands;
